/**
 * `encode/unicode` — four-mode Unicode escape codec.
 *
 * Four escape conventions, all targeting RAW codepoints (so the
 * input is a JS string, not a byte stream — surrogate pairs are
 * handled correctly by iterating with the spread / for…of which
 * yields full codepoints).
 *
 *   js            \u{1f600}     emit \u{...} for non-BMP (>= U+10000)
 *                 \uXXXX        for BMP codepoints (< U+10000)
 *   html-decimal  &#128512;     XML / HTML decimal entity
 *   html-hex      &#x1F600;     XML / HTML hex entity
 *   percent       %F0%9F%98%80  RFC 3986 percent-encoded UTF-8 bytes
 *
 * Differences from the existing `encode/codec` URL helper and
 * `encode/html-entities`:
 *   - codec/url is for whole-URL `encodeURIComponent` semantics
 *     (some chars are reserved). This is "raw" percent: every
 *     non-ASCII codepoint becomes %XX.
 *   - html-entities maps named entities (&copy; &amp;) — this
 *     tool only emits NUMERIC references.
 *
 * Decoding is mode-agnostic: the unescape function recognises all
 * four forms in the same pass, plus extras like `\xNN` and bare
 * `&#...;` that any of the modes might produce.
 */

export type EscapeMode = 'js' | 'html-decimal' | 'html-hex' | 'percent';

export const ESCAPE_MODES: ReadonlyArray<EscapeMode> = [
  'js',
  'html-decimal',
  'html-hex',
  'percent',
];

export const ESCAPE_MODE_LABEL: Record<EscapeMode, string> = {
  js: 'JS \\uXXXX',
  'html-decimal': 'HTML 十进制 &#NN;',
  'html-hex': 'HTML 十六进制 &#xNN;',
  percent: 'Percent %XX',
};

export interface EscapeOpts {
  /** When true (default), only non-ASCII codepoints (>= 0x80) are escaped. */
  onlyNonAscii?: boolean;
}

const ASCII_BOUNDARY = 0x80;

/* ------------------------------------------------------------------ */
/* Encode                                                              */
/* ------------------------------------------------------------------ */

function encodeOne(cp: number, mode: EscapeMode): string {
  switch (mode) {
    case 'js': {
      if (cp < 0x10000) {
        return '\\u' + cp.toString(16).padStart(4, '0');
      }
      return '\\u{' + cp.toString(16) + '}';
    }
    case 'html-decimal':
      return '&#' + cp.toString(10) + ';';
    case 'html-hex':
      return '&#x' + cp.toString(16).toUpperCase() + ';';
    case 'percent': {
      // Encode the codepoint as UTF-8 bytes via TextEncoder, then
      // percent-encode each byte. We construct a 1-codepoint string
      // (using fromCodePoint so non-BMP works) and let the encoder
      // do the heavy lifting.
      const bytes = new TextEncoder().encode(String.fromCodePoint(cp));
      let out = '';
      for (const b of bytes) {
        out += '%' + b.toString(16).toUpperCase().padStart(2, '0');
      }
      return out;
    }
  }
}

export function escapeUnicode(
  text: string,
  mode: EscapeMode,
  opts: EscapeOpts = {},
): string {
  const onlyNonAscii = opts.onlyNonAscii !== false; // default true
  if (!text) return '';

  let out = '';
  // Iterate by codepoint so surrogate pairs are handled correctly.
  for (const ch of text) {
    const cp = ch.codePointAt(0)!;
    if (onlyNonAscii && cp < ASCII_BOUNDARY) {
      out += ch;
    } else {
      out += encodeOne(cp, mode);
    }
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Decode                                                              */
/* ------------------------------------------------------------------ */

/**
 * Decode any combination of the four escape modes back to the
 * original string.
 *
 *   \uXXXX            BMP escape
 *   \u{XXXXX}         non-BMP escape (also accepted: 1-6 hex)
 *   \xXX              ASCII / Latin-1 byte escape
 *   &#NN;             HTML decimal
 *   &#xHH;            HTML hex
 *   %XX               Percent (decoded as a UTF-8 byte sequence —
 *                      we batch contiguous %XX bytes and feed them
 *                      to TextDecoder so multi-byte UTF-8 works)
 *
 * Anything else passes through verbatim.
 */
export function unescapeUnicode(text: string): string {
  if (!text) return '';

  let out = '';
  let i = 0;
  while (i < text.length) {
    const ch = text[i];

    // Backslash escapes (JS literal style)
    if (ch === '\\' && i + 1 < text.length) {
      const next = text[i + 1];
      if (next === 'u' && text[i + 2] === '{') {
        const close = text.indexOf('}', i + 3);
        if (close !== -1) {
          const hex = text.slice(i + 3, close);
          if (/^[0-9a-fA-F]{1,6}$/.test(hex)) {
            out += String.fromCodePoint(parseInt(hex, 16));
            i = close + 1;
            continue;
          }
        }
      }
      if (next === 'u' && /^[0-9a-fA-F]{4}$/.test(text.slice(i + 2, i + 6))) {
        const hex = text.slice(i + 2, i + 6);
        // Handle UTF-16 surrogate pairs: \uD83D\uDE00 → 1 emoji
        const high = parseInt(hex, 16);
        if (
          high >= 0xd800 &&
          high <= 0xdbff &&
          text[i + 6] === '\\' &&
          text[i + 7] === 'u' &&
          /^[0-9a-fA-F]{4}$/.test(text.slice(i + 8, i + 12))
        ) {
          const low = parseInt(text.slice(i + 8, i + 12), 16);
          if (low >= 0xdc00 && low <= 0xdfff) {
            out += String.fromCharCode(high, low);
            i += 12;
            continue;
          }
        }
        out += String.fromCharCode(high);
        i += 6;
        continue;
      }
      if (next === 'x' && /^[0-9a-fA-F]{2}$/.test(text.slice(i + 2, i + 4))) {
        out += String.fromCharCode(parseInt(text.slice(i + 2, i + 4), 16));
        i += 4;
        continue;
      }
    }

    // HTML numeric entities: &#NN; or &#xHH;
    if (ch === '&' && text[i + 1] === '#') {
      const semi = text.indexOf(';', i + 2);
      if (semi !== -1) {
        const body = text.slice(i + 2, semi);
        let cp: number | null = null;
        if (/^[xX][0-9a-fA-F]+$/.test(body)) {
          cp = parseInt(body.slice(1), 16);
        } else if (/^\d+$/.test(body)) {
          cp = parseInt(body, 10);
        }
        if (cp !== null && cp >= 0 && cp <= 0x10ffff) {
          out += String.fromCodePoint(cp);
          i = semi + 1;
          continue;
        }
      }
    }

    // Percent-encoded UTF-8: collect a contiguous run and decode as bytes.
    if (
      ch === '%' &&
      /^[0-9a-fA-F]{2}$/.test(text.slice(i + 1, i + 3))
    ) {
      const bytes: number[] = [];
      let j = i;
      while (
        text[j] === '%' &&
        /^[0-9a-fA-F]{2}$/.test(text.slice(j + 1, j + 3))
      ) {
        bytes.push(parseInt(text.slice(j + 1, j + 3), 16));
        j += 3;
      }
      try {
        out += new TextDecoder('utf-8', { fatal: false }).decode(
          new Uint8Array(bytes),
        );
      } catch {
        // Fall back to raw byte → char (unlikely path; TextDecoder is fatal:false).
        out += bytes.map((b) => String.fromCharCode(b)).join('');
      }
      i = j;
      continue;
    }

    out += ch;
    i++;
  }
  return out;
}
